/**
 * [비즈니스 목적]
 * 전역 테넌트(고객사) 선택 및 관리 Context
 *
 * [워크플로우]
 * 1. 인증 완료 후 API에서 활성 테넌트 목록 조회 (PostgreSQL)
 * 2. localStorage에서 이전 선택 테넌트 복원 (또는 첫 번째 테넌트 자동 선택)
 * 3. 테넌트 변경 시 localStorage에 저장 → 세션 간 유지
 * 4. 선택된 테넌트 정보를 전역 상태로 제공 (다른 컴포넌트에서 useGlobalTenant 훅으로 접근)
 *
 * [주요 기능]
 * - PostgreSQL 기반 테넌트 목록 로드 (API: /api/ontology/tenants)
 * - 멀티 테넌트 코드 지원 (tenant_codes 배열)
 * - localStorage 기반 선택 테넌트 유지
 * - 테넌트 업데이트 이벤트 리스너 (window.addEventListener('tenantsUpdated'))
 * - 테넌트 정보 조회 함수 (getSelectedTenantInfo)
 *
 * [상태 관리]
 * - selectedTenant: 현재 선택된 테넌트 ID (또는 tenant_code)
 * - tenants: 활성 테넌트 목록 배열
 * - loading: 테넌트 목록 로딩 상태
 *
 * [사용 예시]
 * const { selectedTenant, setSelectedTenant, tenants, getSelectedTenantInfo } = useGlobalTenant();
 * const tenantInfo = getSelectedTenantInfo();
 * const tenantCode = tenantInfo?.tenant_code;
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import apiService from '../services/api';

const GlobalTenantContext = createContext();

export const useGlobalTenant = () => {
  const context = useContext(GlobalTenantContext);
  if (!context) {
    throw new Error('useGlobalTenant must be used within a GlobalTenantProvider');
  }
  return context;
};

export const GlobalTenantProvider = ({ children }) => {
  const { currentUser, isAdmin } = useAuth();
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  // 테넌트 목록 로드 (API 서비스 사용)
  useEffect(() => {
    const loadTenants = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // API를 통해 테넌트 목록 로드 중...
        
        const response = await apiService.getAllTenants();
        // 테넌트 목록 로드 완료
        
        if (response.success && response.data) {
          // API 응답에서 활성 테넌트만 필터링
          const activeTenants = response.data.filter(t => t.is_active !== false);
          setTenants(activeTenants);
          // 활성 테넌트 로드됨
        } else {
          console.warn('테넌트 데이터 형식 오류');
          setTenants([]);
        }
      } catch (error) {
        console.error('테넌트 목록 로드 실패:', error.message);
        // 오류가 있어도 빈 배열로 설정하여 UI가 정상 작동하도록 함
        setTenants([]);
      } finally {
        setLoading(false);
      }
    };

    loadTenants();
    
    // 테넌트 업데이트 이벤트 리스너
    const handleTenantsUpdate = () => {
      loadTenants();
    };
    
    window.addEventListener('tenantsUpdated', handleTenantsUpdate);
    
    return () => {
      window.removeEventListener('tenantsUpdated', handleTenantsUpdate);
    };
  }, [currentUser]);

  // 테넌트 선택 변경 핸들러
  const handleTenantChange = (tenant_code) => {
    setSelectedTenant(tenant_code);
    // 로컬 스토리지에 저장하여 세션 간 유지
    localStorage.setItem('selectedGlobalTenant', tenant_code);
  };

  // 테넌트 로드 완료 후 저장된 테넌트 선택 복원 또는 첫 번째 테넌트 선택
  useEffect(() => {
    if (loading || tenants.length === 0) return; // 테넌트 로드가 완료될 때까지 대기

    const savedTenant = localStorage.getItem('selectedGlobalTenant');
    if (savedTenant) {
      // 저장된 테넌트가 현재 로드된 테넌트 목록에 있는지 확인 (PostgreSQL 필드명 사용)
      const isValidTenant = tenants.some(t =>
        // tenant_codes 배열에서 일치하는 코드 검색
        (t.tenant_codes?.some(tc => tc.tenant_code === savedTenant)) ||
        (t.id && t.id === savedTenant)
      );

      if (isValidTenant) {
        setSelectedTenant(savedTenant);
      } else {
        // 유효하지 않은 테넌트면 첫 번째 테넌트 선택하고 localStorage 정리
        setSelectedTenant(tenants[0].id);
        localStorage.removeItem('selectedGlobalTenant');
      }
    } else {
      // 저장된 테넌트가 없으면 첫 번째 테넌트 선택
      setSelectedTenant(tenants[0].id);
    }
  }, [loading, tenants]); // tenants와 loading 상태에 의존

  // 선택된 테넌트 정보 가져오기 (PostgreSQL 필드명 기반)
  const getSelectedTenantInfo = () => {
    // selectedTenant가 없으면 null 반환
    if (!selectedTenant) {
      return null;
    }

    // PostgreSQL 필드명 기반 매칭 - tenant_codes 배열에서 검색
    let foundTenant = tenants.find(t =>
      // tenant_codes 배열에서 일치하는 코드 검색
      (t.tenant_codes?.some(tc => tc.tenant_code === selectedTenant)) ||
      (t.id === selectedTenant)
    );

    // 대소문자 구분 없는 매칭
    if (!foundTenant && selectedTenant && typeof selectedTenant === 'string') {
      const selectedLower = selectedTenant.toLowerCase();
      const selectedUpper = selectedTenant.toUpperCase();

      foundTenant = tenants.find(t =>
        // tenant_codes 배열에서 대소문자 무시하고 검색
        (t.tenant_codes?.some(tc =>
          tc.tenant_code && (tc.tenant_code.toLowerCase() === selectedLower || tc.tenant_code.toUpperCase() === selectedUpper)
        )) ||
        (t.id && typeof t.id === 'string' && (t.id.toLowerCase() === selectedLower || t.id.toUpperCase() === selectedUpper))
      );
    }

    // 테넌트 정보 반환 - PostgreSQL 스키마 기반
    if (foundTenant) {
      // 첫 번째 테넌트 코드 추출 (멀티 코드 지원)
      const primaryCode = foundTenant.tenant_codes?.find(tc => tc.is_primary)?.tenant_code ||
                         foundTenant.tenant_codes?.[0]?.tenant_code ||
                         foundTenant.tenant_code ||
                         foundTenant.id;

      return {
        id: foundTenant.id,
        name: foundTenant.tenant_name || '이름 없음',
        tenant_name: foundTenant.tenant_name || '이름 없음',
        tenant_code: primaryCode,
        tenant_codes: foundTenant.tenant_codes || [],
        description: foundTenant.description || '',
        logo_url: foundTenant.logo_url || '',
        is_active: foundTenant.is_active !== false,
        ...foundTenant
      };
    }

    return {
      id: selectedTenant,
      name: '알 수 없음',
      tenant_name: '알 수 없음',
      tenant_code: selectedTenant
    };
  };

  const value = {
    selectedTenant,
    setSelectedTenant: handleTenantChange,
    tenants,
    loading,
    getSelectedTenantInfo
  };

  return (
    <GlobalTenantContext.Provider value={value}>
      {children}
    </GlobalTenantContext.Provider>
  );
};

export default GlobalTenantContext;